import React, { useState, useEffect } from "react";
import {
  BsX,
  BsCheck,
  BsPerson,
  BsChevronDown,
  BsChevronUp,
  BsExclamationTriangle,
} from "react-icons/bs";
import { toast } from "react-toastify";
import { useMemberStore } from "../../store/memberStore";
import { FiCalendar } from "react-icons/fi";
import { CiEdit } from "react-icons/ci";
import { getClientName } from "../../utils/clientInfo";
import { formatSalonDateTime } from "../../utils/salonTime";

const AssignServiceModal = ({
  isAssignModalOpen,
  closeAssignModal,
  selectedRdv,
}) => {
  const {
    isLoadingAvailableEmployee,
    availableEmployee,
    getAvailableEmployee,
    assignLoading,
    assignServiceToEmployee,
    reassignLoading,
    reassignServiceToEmployee,
    cancelAssignLoading,
    cancelAssignServiceToEmployee,
  } = useMemberStore();

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [yetAssignSelected, setYetAssignSelected] = useState(null);
  const [isReAssign, setIsReAssign] = useState(false);
  const [cancelConfirmModal, setCancelConfirmModal] = useState({
    isOpen: false,
    assignmentId: null,
    employeeName: null,
    rdvId: null,
  });

  // Réinitialiser la sélection quand le modal s'ouvre
  useEffect(() => {
    if (isAssignModalOpen && selectedRdv?.date) {
      // console.log({ selectedRdv });
      getAvailableEmployee(selectedRdv.date);
      setSelectedEmployees([]);
      setSearchTerm("");
      setIsDropdownOpen(false);
      setIsReAssign(false);
      setYetAssignSelected(null);

      // console.log("selectedRdv.assignments : ", selectedRdv.assignments)
    }
  }, [isAssignModalOpen, selectedRdv]);

  // Ouvrir la modale de confirmation d'annulation
  const openCancelConfirm = (assignmentId, employeeName, rdvId) => {
    setCancelConfirmModal({
      isOpen: true,
      assignmentId,
      employeeName,
      rdvId,
    });
  };

  // Fermer la modale de confirmation
  const closeCancelConfirm = () => {
    setCancelConfirmModal({
      isOpen: false,
      assignmentId: null,
      employeeName: null,
      rdvId: null,
    });
  };

  // Gérer l'annulation confirmée
  const handleCancelAssign = async () => {
    if (!cancelConfirmModal.assignmentId) return;

    setYetAssignSelected(cancelConfirmModal.assignmentId);
    await cancelAssignServiceToEmployee(
      cancelConfirmModal.assignmentId,
      selectedRdv.id
    );
    closeCancelConfirm();
  };

  // Filtrer les employés selon la recherche
  const filteredEmployees = availableEmployee.filter(
    (employee) =>
      employee.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAssign = async () => {
    if (selectedEmployees.length === 0 || !selectedRdv) {
      toast.error("Veuillez sélectionner au moins un employé");
      return;
    }

    const employeeIds = selectedEmployees.map((emp) => emp.id);

    // console.log({ employeeIds })
    // console.log("selectedRdv.id: ", selectedRdv.id)
    try {
      if (isReAssign) {
        if (!yetAssignSelected) {
          toast.info("Employé non identifié");
          return;
        }
        await reassignServiceToEmployee(
          yetAssignSelected,
          employeeIds,
          selectedRdv.id
        );
        setYetAssignSelected(null);
        setIsReAssign(false);
      } else {
        await assignServiceToEmployee(selectedRdv.id, employeeIds);
      }

      closeAssignModal();
    } catch (error) {
      // L'erreur est déjà gérée dans le store
    } finally {
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployees((prev) => {
      if (isReAssign) {
        return [employee];
      }

      const isAlreadySelected = prev.some((emp) => emp.id === employee.id);
      if (isAlreadySelected) {
        return prev.filter((emp) => emp.id !== employee.id);
      } else {
        return [...prev, employee];
      }
    });
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  const removeEmployee = (employeeId) => {
    setSelectedEmployees((prev) => prev.filter((emp) => emp.id !== employeeId));
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (!isAssignModalOpen || !selectedRdv || !selectedRdv?.date) return null;

  const formatDate = (dateString) =>
    formatSalonDateTime(dateString, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "CONFIRMED":
        return "bg-green-100 text-green-800 border-green-200";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200";
      case "COMPLETED":
        return "bg-secondary-100 text-secondary-800 border-secondary-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDING: "En attente",
      CONFIRMED: "Confirmé",
      CANCELLED: "Annulé",
      COMPLETED: "Terminé",
    };
    return labels[status] || status;
  };

  const totalServices = selectedRdv.appointmentServices?.length || 0;
  const totalAmount = parseFloat(selectedRdv.totalAmount || 0);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* En-tête */}
          <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200">
            <div>
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                {selectedRdv.assignments.length > 0 ? "Réassigner" : "Assigner"}{" "}
                des employés
              </h2>
              <p className="font-nunito text-[14px] text-text mt-1">
                Sélectionnez un ou plusieurs employés pour ce service
              </p>
            </div>
            <button
              onClick={closeAssignModal}
              className="p-2 hover:bg-gray-100 transition-colors"
              disabled={assignLoading || reassignLoading}
            >
              <BsX size={24} />
            </button>
          </div>

          <div className="w-full max-w-2xl max-h-[60vh] overflow-y-auto">
            {/* Informations du service */}
            <div className="w-full px-4 sm:px-6 py-4">
              <div className="flex flex-row lg:items-center lg:justify-between gap-4 mb-2">
                {/* Côté gauche - Informations principales */}
                <div className="md:flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                  {/* Badge ID - Visible sur tous les écrans */}
                  <div className="md:w-10 md:h-10 md:bg-gradient-to-br md:from-primary md:to-secondary flex items-center md:justify-center flex-shrink-0">
                    <span className="text-primary md:text-white font-poppins font-bold text-[16px] text-xs sm:text-sm">
                      #{selectedRdv.code?.split("_")[1] || selectedRdv.id}
                    </span>
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 min-w-0">
                    {/* Ligne 1: Code réservation + Statut + Prix (mobile) */}
                    <div className="flex flex-col xs:flex-row xs:items-center gap-2 mb-2">
                      <div className="flex-row flex items-center justify-between gap-2 flex-1 min-w-0">
                        <h3 className="font-poppins font-semibold text-[18px] text-title truncate flex-row items-center">
                          <span className="hidden md:inline-flex mr-1">
                            Réservation{" "}
                          </span>
                          <span>
                            {selectedRdv.code || `#${selectedRdv.id}`}
                          </span>
                        </h3>

                        {/* Statut - Toujours visible */}
                        <span
                          className={`px-2 py-1 font-nunito text-[14px] font-medium border ${getStatusColor(
                            selectedRdv.status
                          )} flex-shrink-0`}
                        >
                          {getStatusLabel(selectedRdv.status)}
                        </span>
                      </div>

                      {/* Prix - Visible*/}
                      <div className="flex md:hidden justify-between items-center">
                        <span className="font-poppins font-bold text-[16px] text-montant text-sm">
                          {totalAmount.toFixed(0)} FCFA
                        </span>
                      </div>
                    </div>

                    {/* Ligne 2: Date et services */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                      {/* Date - Format adaptatif */}
                      <div className="flex items-center gap-1 font-nunito text-[14px] text-text">
                        <FiCalendar size={14} className="flex-shrink-0" />
                        <span className="text-xs sm:text-sm">
                          {formatDate(selectedRdv.date)}
                        </span>
                      </div>

                      {/* Services - Format adaptatif */}
                      <div className="flex items-center gap-2">
                        {/* <span className="text-xs sm:text-sm text-gray-500">
                                                {totalServices} service{totalServices > 1 ? 's' : ''}
                                            </span> */}

                        {/* Séparateur - visible seulement sur desktop */}
                        <span className="hidden sm:inline text-gray-300">
                          •
                        </span>

                        {/* Durée estimée - visible seulement sur desktop */}
                        {selectedRdv.estimatedDuration && (
                          <span className="hidden sm:inline font-nunito text-[14px] text-text text-xs">
                            {selectedRdv.estimatedDuration} min
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Informations client - visible seulement sur tablette/desktop */}
                    <div className="hidden sm:flex items-center gap-1 mt-2">
                      <span className="font-nunito text-[14px] text-text text-xs">
                        Client: {getClientName(selectedRdv)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Côté droit - Informations financières (visible à partir de tablette) */}
                <div className="hidden sm:flex flex-col items-end flex-shrink-0">
                  <p className="font-poppins font-bold text-[16px] text-montant text-lg">
                    {totalAmount.toFixed(2)} FCFA
                  </p>
                  <div className="flex items-center gap-2 font-nunito text-[14px] text-text text-sm">
                    {/* <span>{totalServices} service{totalServices > 1 ? 's' : ''}</span> */}
                    {selectedRdv.estimatedDuration && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{selectedRdv.estimatedDuration} min</span>
                      </>
                    )}
                  </div>

                  {/* Note client si disponible - visible seulement sur desktop */}
                  {selectedRdv.notes && (
                    <div className="hidden lg:block max-w-xs mt-2">
                      <p
                        className="font-nunito text-[14px] text-text text-xs truncate"
                        title={selectedRdv.notes}
                      >
                        {selectedRdv.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="font-nunito text-[14px] text-text text-xs border-b border-gray-200 py-2">
                <h3 className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  <span className="font-poppins font-semibold text-[18px] text-title">Services</span>
                  <span className="bg-gray-100 px-2 ms-1 w-fit">
                    {totalServices}
                  </span>
                </h3>

                <div className="">
                  {selectedRdv.appointmentServices?.map(
                    (appointmentService, index) => (
                      <p key={index} className="font-poppins font-medium text-[15px] text-subtitle">
                        <span>
                          {appointmentService?.quantity} {" x "}
                        </span>
                        {appointmentService?.service?.nom || "Service inconnu"}
                      </p>
                    )
                  )}
                </div>
              </div>

              {/* Informations supplémentaires pour mobile */}
              <div className="sm:hidden mt-3 pt-3 border-t border-gray-200">
                {/* Informations client - mobile */}
                <div className="flex items-center gap-2 mb-2 font-nunito text-[14px] text-text text-xs">
                  <span className="text-action font-medium">
                    Nom :
                  </span>
                  <span className="text-xs text-gray-500">
                    {getClientName(selectedRdv)}
                  </span>
                </div>

                {/* Note client - mobile */}
                {selectedRdv.notes && (
                  <div className="bg-gray-50 p-2">
                    <p className="font-nunito text-[14px] text-text text-xs">{selectedRdv.notes}</p>
                  </div>
                )}

                {/* Durée estimée - mobile */}
                {selectedRdv.estimatedDuration && (
                  <div className="flex items-center gap-2 mt-2 font-nunito text-[14px] text-text text-xs">
                    <span className="text-action font-medium">
                      Durée :
                    </span>
                    <span className="text-gray-500">
                      {selectedRdv.estimatedDuration} min
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sélection des employés */}
            {selectedRdv.assignments.length !== 0 && (
              <div className="px-6">
                <div className="mb-4">
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                    Employés Assignés
                  </label>

                  <div className="flex flex-wrap gap-2 font-nunito text-[14px] text-text text-xs">
                    {selectedRdv?.assignments.map((rdv) => (
                      <div
                        key={rdv.id}
                        className="bg-secondary-100 text-secondary-800 px-3 py-2 flex items-center gap-2 text-xs"
                      >
                        <span className="">
                          {rdv?.employee?.prenom} {rdv?.employee?.nom}
                        </span>

                        <div className="ms-3 flex items-center gap-1 text-xs">
                          {!isReAssign && (
                            <button
                              disabled={reassignLoading}
                              title="Modifier"
                              type="button"
                              onClick={() => {
                                setIsReAssign(true);
                                setYetAssignSelected(rdv.id);
                                setSelectedEmployees([]);
                                toggleDropdown();
                              }}
                              className="text-secondary-600 hover:text-secondary-800 text-xs p-1 hover:bg-secondary-200 transition-colors"
                            >
                              {reassignLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-secondary-600"></div>
                              ) : (
                                <CiEdit size={14} />
                              )}
                            </button>
                          )}
                          <button
                            disabled={cancelAssignLoading}
                            title="Annuler l'assignation"
                            type="button"
                            onClick={() =>
                              openCancelConfirm(
                                rdv.id,
                                `${rdv?.employee?.prenom} ${rdv?.employee?.nom}`,
                                selectedRdv.id
                              )
                            }
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 transition-colors duration-200 disabled:opacity-50"
                          >
                            {cancelAssignLoading &&
                              yetAssignSelected === rdv.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600"></div>
                            ) : (
                              <BsX size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="px-6">
              <div className="mb-4">
                <div
                  className={`flex justify-between ${isReAssign ? "mb-2" : ""}`}
                >
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                    {selectedRdv.assignments.length !== 0
                      ? `${isReAssign
                        ? "Modifier l'assignation"
                        : "Ajouter des employés"
                      }`
                      : "Sélectionner des employés"}
                  </label>

                  {isReAssign && (
                    <button
                      disabled={reassignLoading}
                      title="Modifier"
                      type="button"
                      onClick={() => {
                        setIsReAssign(false);
                        setYetAssignSelected(null);
                        setSelectedEmployees([]);
                        setIsDropdownOpen(false);
                      }}
                      className="text-white text-xs p-1 bg-secondary-200 transition-colors font-nunito font-semibold text-[14px]"
                    >
                      Annuler
                    </button>
                  )}
                </div>

                {/* Dropdown pour sélection multiple */}
                <div className="relative font-nunito text-[14px] text-text text-xs">
                  {/* Bouton du dropdown */}
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 bg-white text-left flex justify-between items-center"
                  >
                    <span className="text-gray-500">
                      {selectedEmployees.length === 0
                        ? `${selectedRdv.assignments.length !== 0
                          ? "Ajouter "
                          : "Sélectionner "
                        } un ou plusieurs employés...`
                        : `${selectedEmployees.length} employé(s) sélectionné(s)`}
                    </span>
                    {isDropdownOpen ? <BsChevronUp /> : <BsChevronDown />}
                  </button>

                  {/* Dropdown menu */}
                  {isDropdownOpen && (
                    <div className="w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {/* Barre de recherche dans le dropdown */}
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          placeholder="Rechercher un employé..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-secondary-500 font-nunito text-[14px] text-text"
                        />
                      </div>

                      {/* Liste des employés */}
                      <div className="py-2">
                        {isLoadingAvailableEmployee ? (
                          <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-secondary-500"></div>
                            <span className="ml-2 font-nunito text-[14px] text-text text-sm">
                              Chargement...
                            </span>
                          </div>
                        ) : filteredEmployees.length === 0 ? (
                          <div className="px-3 py-4 text-center font-nunito text-[14px] text-text text-sm">
                            Aucun employé trouvé
                          </div>
                        ) : (
                          filteredEmployees.map((employee) => {
                            const isSelected = selectedEmployees.some(
                              (emp) => emp.id === employee.id
                            );
                            const isAvailable = !employee.has_appointement;

                            return (
                              <div
                                key={employee.id}
                                className={`z-50 px-3 py-2 cursor-pointer flex items-center justify-between ${isSelected
                                  ? "bg-secondary-50 text-secondary-800"
                                  : "hover:bg-gray-50"
                                  } ${!isAvailable ? "opacity-50" : ""}`}
                                onClick={() =>
                                  handleEmployeeSelect(employee)
                                }
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  {/* Avatar */}
                                  <div className="w-8 h-8 bg-secondary-100 flex items-center justify-center flex-shrink-0">
                                    <BsPerson className="text-secondary-600 text-sm" />
                                  </div>

                                  {/* Informations de l'employé */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-poppins font-medium text-[15px] text-subtitle truncate">
                                        {employee.prenom} {employee.nom}
                                      </h4>
                                      {!isAvailable && (
                                        <span className="font-nunito text-[14px] text-xs bg-red-100 text-red-800 px-2 py-1">
                                          Occupé
                                        </span>
                                      )}
                                    </div>
                                    <p className="font-nunito text-[14px] text-text text-xs truncate">
                                      {employee.email}
                                    </p>
                                  </div>
                                </div>

                                {/* Indicateur de sélection et disponibilité */}
                                <div className="flex items-center gap-2">
                                  {isAvailable ? (
                                    <span className="font-nunito text-[14px] text-xs bg-green-100 text-green-800 px-2 py-1">
                                      Disponible
                                    </span>
                                  ) : (
                                    <span className="font-nunito text-[14px] text-xs bg-red-100 text-red-800 px-2 py-1">
                                      Non disponible
                                    </span>
                                  )}
                                  {isSelected && (
                                    <BsCheck
                                      className="text-secondary-600 flex-shrink-0"
                                      size={16}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Employés sélectionnés */}
                {selectedEmployees.length > 0 && (
                  <div className="mt-3">
                    <p className="font-poppins font-medium text-[15px] text-subtitle mb-2 text-sm">
                      Employés sélectionnés ({selectedEmployees.length}) :
                    </p>
                    <div className="flex flex-wrap gap-2 font-nunito text-[14px] text-text text-xs">
                      {selectedEmployees.map((employee) => (
                        <div
                          key={employee.id}
                          className="bg-secondary-100 text-secondary-800 px-3 py-2 flex items-center gap-2 text-xs"
                        >
                          <span>
                            {employee.prenom} {employee.nom}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeEmployee(employee.id)}
                            className="text-secondary-600 hover:text-secondary-800 text-xs p-1 hover:bg-secondary-200 transition-colors"
                          >
                            <BsX size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-200">
            <button
              type="button"
              onClick={closeAssignModal}
              disabled={
                selectedRdv.assignments.length !== 0
                  ? reassignLoading
                  : assignLoading
              }
              className="font-nunito font-semibold text-[14px] px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={
                (selectedRdv.assignments.length !== 0 &&
                  selectedEmployees.length === 0) ||
                assignLoading ||
                selectedEmployees.length === 0
              }
              className="font-nunito font-semibold text-[14px] px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {(reassignLoading || assignLoading) && (
                <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white"></div>
              )}
              {assignLoading
                ? `${selectedRdv.assignments.length > 0 &&
                  selectedEmployees.length > 0
                  ? "Ajout"
                  : "Assignation"
                }...`
                : `${selectedRdv.assignments.length > 0
                  ? `${isReAssign ? "Modifier" : "Ajouter"}`
                  : "Assigner"
                }  (${selectedEmployees.length})`}
            </button>
          </div>
        </div>
      </div>

      {/* Modale de confirmation pour l'annulation d'assignation */}
      {cancelConfirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white max-w-md w-full mx-auto">
            {/* En-tête */}
            <div className="flex items-center gap-3 p-6 border-b border-gray-200">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 flex items-center justify-center">
                <BsExclamationTriangle className="text-red-600 text-lg" />
              </div>
              <div>
                <h3 className="font-poppins font-semibold text-[18px] text-title">
                  Annuler l'assignation
                </h3>
                <p className="font-nunito text-[14px] text-text mt-1">
                  Confirmez-vous cette action ?
                </p>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="bg-yellow-50 border border-yellow-200 p-4">
                <p className="font-nunito text-[14px] text-text">
                  ⚠️ <strong>Action irréversible</strong>
                </p>
                <p className="font-nunito text-[14px] text-text mt-1">
                  L'employé sera désassigné de ce rendez-vous. Cette action ne
                  peut pas être annulée.
                </p>
              </div>

              {cancelConfirmModal.employeeName && (
                <div className="mt-4 p-3 bg-gray-50">
                  <p className="font-nunito text-[14px] text-text">
                    <strong>Rendez-vous :</strong> #{cancelConfirmModal.rdvId}
                  </p>
                  <p className="font-nunito text-[14px] text-text mt-1">
                    <strong>Employé :</strong> {cancelConfirmModal.employeeName}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeCancelConfirm}
                disabled={cancelAssignLoading}
                className="px-4 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 font-nunito font-semibold text-[14px]"
              >
                Retour
              </button>
              <button
                onClick={handleCancelAssign}
                disabled={cancelAssignLoading}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 flex items-center gap-2 font-nunito font-semibold text-[14px]"
              >
                {cancelAssignLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Annulation...
                  </>
                ) : (
                  <>
                    <BsX size={16} />
                    Confirmer l'annulation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignServiceModal;